import type { StepOptions } from 'shepherd.js'
import { accountDetailIntroTour } from './account-detail/intro'
import { shareDialogIntroTour } from './account-detail/share-dialog'
import { accountsIntroTour } from './accounts/intro'
import { demodayOrientationTour } from './demoday/orientation'
import { inboxIntroTour } from './inbox/intro'
import { marketResearchIntroTour } from './market-research/intro'

export interface TourDefinition {
  /** Stable identifier — used as PostHog event property and localStorage key. */
  id: string
  /** Human-readable label for the dev panel + future settings UI. */
  displayName: string
  /** PostHog feature flag gating this tour. If the flag is off, the tour never runs. */
  featureFlag: string
  /** Shepherd step definitions. Each step must set `id` so funnel events can reference it. */
  steps: StepOptions[]
  /**
   * 'auto' tours start on first mount of a page where the flag is enabled and
   * the seen-key is not set. 'manual' tours only start via `startTour()`.
   */
  trigger: 'auto' | 'manual'
  /**
   * Page where this tour is triggered (used by the dev tour switcher to navigate
   * before starting). Optional — manual tours or multi-page flows can omit it.
   */
  targetPath?: string
  /**
   * Bump to re-deliver a meaningfully-changed tour to users who already
   * completed an earlier version. Seen-key becomes `tour-<id>-seen-v<N>` for
   * N > 1; v1 keeps the legacy `tour-<id>-seen` key so existing dismissals
   * aren't lost.
   */
  seenKeyVersion?: number
}

export const TOUR_REGISTRY: Record<string, TourDefinition> = {
  [inboxIntroTour.id]: inboxIntroTour,
  [accountsIntroTour.id]: accountsIntroTour,
  [accountDetailIntroTour.id]: accountDetailIntroTour,
  [marketResearchIntroTour.id]: marketResearchIntroTour,
  [shareDialogIntroTour.id]: shareDialogIntroTour,
  [demodayOrientationTour.id]: demodayOrientationTour,
}

/**
 * Per-device localStorage key for "user has seen / dismissed this tour".
 * v1 uses the legacy key shape so we don't re-fire already-completed tours
 * for existing users. v2+ namespaces the version so a bumped tour redelivers.
 */
function tourSeenKey(tour: Pick<TourDefinition, 'id' | 'seenKeyVersion'>): string {
  const v = tour.seenKeyVersion ?? 1
  return v > 1 ? `tour-${tour.id}-seen-v${v}` : `tour-${tour.id}-seen`
}

/**
 * In-memory fallback for browsers where localStorage throws (private mode,
 * disabled storage, quota exceeded). Persists for the JS module lifetime —
 * i.e. one tab, until reload. Without this, a private-mode user who
 * dismisses a tour would re-see it on every page navigation in the same
 * session because the seen-key never sticks in localStorage.
 *
 * Trade-off: a private-mode user gets the tour once per tab instead of
 * once per device. Acceptable; the alternative is annoying repeat-firing.
 */
const sessionSeen = new Set<string>()

export function isTourSeen(tour: Pick<TourDefinition, 'id' | 'seenKeyVersion'>): boolean {
  if (sessionSeen.has(tour.id)) {
    return true
  }
  try {
    return localStorage.getItem(tourSeenKey(tour)) === 'true'
  }
  catch {
    return false
  }
}

export function markTourSeen(tour: Pick<TourDefinition, 'id' | 'seenKeyVersion'>): void {
  sessionSeen.add(tour.id)
  try {
    localStorage.setItem(tourSeenKey(tour), 'true')
  }
  catch {
    // localStorage unavailable — in-memory fallback above keeps us correct
    // within this tab.
  }
}

export function clearTourSeen(tour: Pick<TourDefinition, 'id' | 'seenKeyVersion'>): void {
  sessionSeen.delete(tour.id)
  try {
    localStorage.removeItem(tourSeenKey(tour))
  }
  catch {
    // localStorage unavailable — Set delete above is sufficient.
  }
}
